// src/api/client.js
import axios from 'axios';
import { API_BASE_URL } from '../config';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('login');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const equipmentAPI = {
  getAll:        (params)   => client.get('/equipment/', { params }),
  exportData:    (params, format) => client.get('/equipment/export', { params: { ...params, format }, responseType: 'blob' }),
  getOne:        (id)       => client.get(`/equipment/${id}`),
  getStats:      ()         => client.get('/equipment/stats'),
  getStatuses:   ()         => client.get('/equipment/statuses'),
  getNextNumber: (prefix)   => client.get(`/equipment/next-inventory-number/${prefix}`),
  create:        (data)     => client.post('/equipment/', data),
  update:        (id, data) => client.patch(`/equipment/${id}`, data),
  performAction: (id, data) => client.post(`/equipment/${id}/action`, data),
  bulkAction:    (data)     => client.post('/equipment/bulk-action', data),
};

export const employeesAPI = {
  getAll:       (params)   => client.get('/employees/', { params }),
  exportData:   (params, format) => client.get('/employees/export', { params: { ...params, format }, responseType: 'blob' }),
  getOne:       (id)       => client.get(`/employees/${id}`),
  getTypeStats: ()         => client.get('/employees/type-stats'),
  create:       (data)     => client.post('/employees/', data),
  update:       (id, data) => client.patch(`/employees/${id}`, data),
};

export const consumablesAPI = {
  // Остатки и журнал
  getStock:           (params) => client.get('/consumables/stock', { params }),
  exportStock:        (params, format) => client.get('/consumables/stock/export', { params: { ...params, format }, responseType: 'blob' }),
  getLog:             (params) => client.get('/consumables/log', { params }),
  exportLog:          (params, format) => client.get('/consumables/log/export', { params: { ...params, format }, responseType: 'blob' }),

  // Справочники
  getClassifications: ()       => client.get('/consumables/classifications'),
  getCategories:      (params) => client.get('/consumables/categories', { params }),
  getItems:           (params) => client.get('/consumables/items', { params }),

  // Позиции справочника
  createItem:         (data)   => client.post('/consumables/items', data),
  updateItem:         (id, data) => client.patch(`/consumables/items/${id}`, data),

  // Движения
  addMovement:        (data)   => client.post('/consumables/movement', data),
  addBulkMovement:    (data)   => client.post('/consumables/movement/bulk', data),

  // Аналитика
  getAnalytics:       (params) => client.get('/consumables/analytics', { params }),
};

export const dictAPI = {
  getCategories:  ()            => client.get('/dict/categories'),
  getTypes:       (category_id) => client.get('/dict/types', { params: { category_id } }),
  getLocations:   ()            => client.get('/dict/locations'),
  getCompanies:   ()            => client.get('/dict/companies'),
  getCfo:         ()            => client.get('/dict/cfo'),
  createCategory: (data)        => client.post('/dict/categories', data),
  createType:     (data)        => client.post('/dict/types', data),
  createLocation: (data)        => client.post('/dict/locations', data),
  createCompany:  (data)        => client.post('/dict/companies', data),
};

export const syncAPI = {
  runTribune:    () => client.post('/sync/tribune'),
  statusTribune: () => client.get('/sync/tribune/status'),
};

export const actsAPI = {
  getSigners:   (company_id)  => client.get('/acts/signers', { params: { company_id } }),
  createSigner: (data)        => client.post('/acts/signers', data),
  updateSigner: (id, data)    => client.patch(`/acts/signers/${id}`, data),
  generate:     (data)        => client.post('/acts/generate', data),
  download:     (act_id)      => client.get(`/acts/download/${act_id}`, { responseType: 'blob' }),
  list:         (employee_id) => client.get('/acts/list', { params: { employee_id } }),
};

export const returnActsAPI = {
  getSigners: (company_id)  => client.get('/acts/signers', { params: { company_id } }),
  generate:   (data)        => client.post('/return-acts/generate', data),
  list:       (employee_id) => client.get('/return-acts/list', {
    params: employee_id ? { employee_id } : {},
  }),
  view:       (act_id)      => client.get(`/return-acts/view/${act_id}`),
};

export const usersAPI = {
  getAll:  ()            => client.get('/auth/users'),
  create:  (data)        => client.post('/auth/users', data),
  update:  (id, data)    => client.patch(`/auth/users/${id}`, data),
  remove:  (id)          => client.delete(`/auth/users/${id}`),
};

export const servicesAPI = {
  getCategories:     (params)   => client.get('/services/categories', { params }),
  createCategory:    (data)     => client.post('/services/categories', data),
  updateCategory:    (id, data) => client.patch(`/services/categories/${id}`, data),
  getAll:            (params)   => client.get('/services/', { params }),
  exportList:        (params, format) => client.get('/services/export', { params: { ...params, format }, responseType: 'blob' }),
  exportDetail:      (id, params) => client.get(`/services/${id}/export`, { params, responseType: 'blob' }),
  getOne:            (id)       => client.get(`/services/${id}`),
  getServiceAudit:   (id, params) => client.get(`/services/${id}/audit`, { params }),
  createService:     (data)     => client.post('/services/', data),
  updateService:     (id, data) => client.patch(`/services/${id}`, data),
  getStats:          ()         => client.get('/services/stats/summary'),
  getExpiring:       (days)     => client.get('/services/expiring', { params: { days } }),
  getAnalytics:      (params)   => client.get('/services/analytics', { params }),
  exportAnalytics:   (params)   => client.get('/services/analytics/export', { params, responseType: 'blob' }),
  getAssignments:    (params)   => client.get('/services/assignments/list', { params }),
  assign:            (data)     => client.post('/services/assignments', data),
  revoke:            (id, data) => client.patch(`/services/assignments/${id}/revoke`, data),
  getLicensePurchases:(id)      => client.get(`/services/license-groups/${id}/purchases`),
  createLicensePurchase:(id, data) => client.post(`/services/license-groups/${id}/purchases`, data),
  createServiceLicensePurchase:(serviceId, data) => client.post(`/services/${serviceId}/purchases`, data),
  updateLicensePurchase:(groupId, purchaseId, data) =>
    client.patch(`/services/license-groups/${groupId}/purchases/${purchaseId}`, data),
};

// ИСПРАВЛЕНО: роут переехал с /auth/employees/{id}/access на /permissions/roles/{id}

export const itAnalyticsAPI = {
  getSummary:      ()       => client.get('/it-analytics/summary'),
  getAnalytics:    (params) => client.get('/it-analytics/analytics', { params }),
  getRequests:     (params) => client.get('/it-analytics/requests', { params }),
  exportRequests:  (params, format) => client.get('/it-analytics/requests/export', {
    params: { ...params, format },
    responseType: 'blob',
  }),
};

export const accessAPI = {
  updateEmployeeAccess: (id, data) => client.patch(`/permissions/roles/${id}`, data),
};

export default client;